import { router } from '@inertiajs/vue3'

import { stripMicrositePrefix } from './useLangPath.js'

// SessionStorage key under which the visitor's most recent property-search
// URL is parked, so the next property-details page can swap its breadcrumb
// back-link to that URL (preserving query string, filters, pagination).
export const LAST_SEARCH_KEY = 'nt_last_property_search'

// Matches the property-search URL shape — `(commercial-|student-)?(sales|lettings)`
// optionally followed by bedroom / property-type / location / price segments.
// Kept in step with PATH_REGEX in mixins/property-search-results/constants.js:
// a search this misses is one the visitor cannot be sent back to.
// Permissive on purpose: the bare landing URL `/sales/property-for-sale` and
// the fully-loaded `/commercial-lettings-map/2-bedroom/flat-for-rent/in-leeds/from-1500?page=3`
// both qualify as "this is a search results page worth remembering".
export const SEARCH_REGEX =
  /^(?:(?<division>commercial)-|(?<student>student)-)?(?<department>sales|lettings)(?:-map)?(?:\/\d+-bedroom)?(?:\/[A-Za-z-]+-for-(?:sale|rent))?(?:\/in-[A-Za-z0-9,.-]+)?(?:\/(?:between-\d+-\d+|from-\d+|to-\d+))?$/

/**
 * Strip a leading /xx language prefix and any leading slash before matching
 * against SEARCH_REGEX, since the regex is written for the un-prefixed shape.
 */
export function stripLangAndLead(pathname) {
  return pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '').replace(/^\/+/, '')
}

/**
 * Match a pathname against SEARCH_REGEX. An office microsite serves its search
 * under its own segment (`/derby/lettings/...`), which is taken off first —
 * only that known segment, so an ordinary page such as `/services/lettings` is
 * never mistaken for a search.
 */
export function matchSearchPath(pathname, micrositePrefix = router.page?.props?.website?.path_prefix) {
  return stripLangAndLead(stripMicrositePrefix(pathname, micrositePrefix || null)).match(SEARCH_REGEX)
}

export function labelForSearch(department, isCommercial, isStudent = false) {
  if (isStudent && department === 'rent') return 'Student Property To Rent'
  if (isCommercial && department === 'rent') return 'Commercial Property To Rent'
  if (isCommercial) return 'Commercial Property For Sale'
  if (department === 'rent') return 'Property To Rent'
  return 'Property For Sale'
}

/**
 * If the given pathname is a property-search URL, persist the full
 * pathname + search string into sessionStorage so the next property-details
 * page can offer it as a back-link.
 *
 * Wrapped in try/catch because sessionStorage throws under private browsing
 * and quota-exceeded conditions — the canonical fallback the server set is
 * fine in those cases.
 */
export function captureCurrentSearchUrl() {
  if (typeof window === 'undefined') return
  if (!matchSearchPath(window.location.pathname)) return
  try {
    sessionStorage.setItem(LAST_SEARCH_KEY, window.location.pathname + window.location.search)
  } catch {
    // ignore
  }
}

/**
 * Read the last-remembered search URL out of sessionStorage and return
 * `{ href, label }` (label derived from the URL's department/division), or
 * null when nothing is stored or the stored value no longer matches the
 * search shape.
 */
export function readRememberedSearch() {
  if (typeof window === 'undefined') return null
  let raw = null
  try {
    raw = sessionStorage.getItem(LAST_SEARCH_KEY)
  } catch {
    return null
  }
  if (!raw) return null
  const path = raw.split('?')[0]
  const match = matchSearchPath(path)
  if (!match) return null
  const department = match.groups?.department === 'lettings' ? 'rent' : 'sale'
  const isCommercial = match.groups?.division === 'commercial'
  return { href: raw, label: labelForSearch(department, isCommercial, !!match.groups?.student) }
}

/**
 * Install a page-lifetime listener that captures every search-results URL
 * the visitor lands on (including same-tab Inertia navigations). Returns a
 * teardown function. Idempotent — calling twice will install two listeners,
 * so prefer to mount it from a single root component.
 */
export function installSearchHistoryCapture() {
  captureCurrentSearchUrl()
  return router.on('navigate', () => captureCurrentSearchUrl())
}
